/**
 * AgentConfigPanel — runtime editor for the env-wide Ion Agent Config.
 * =========================================================================
 * Surfaces the backend `/admin/chat-management/agent-config` contract:
 *   - temperature_default — Bedrock sampling temperature (0.0–1.0)
 *   - model_default       — default Bedrock inference profile (IAM-bounded
 *                            <select>, populated from `allowed_models`)
 *   - max_output_tokens / max_iterations — agent-loop bounds
 *   - cache_ttl_default / cache_ttl_admin — prompt-cache TTL ("5m" | "1h")
 *   - websearch_max_per_turn / websearch_max_per_conversation — web-search caps
 *   - rate_limit_per_minute — /chat/stream requests-per-minute cap
 *
 * ENV-WIDE ONLY — no per-user dimension (locked decision, see
 * .planning/ai-settings-config-dashboard-plan.md §0.1). The per-user table /
 * EditDrawer in the Usages subtab are untouched by this panel; they still
 * edit USD/request limits only.
 *
 * All 9 fields above are LIVE on the request path as of 2026-07-17 (Fable
 * review #8, HIGH): max_output_tokens/max_iterations/cache TTLs/web-search
 * caps/rate_limit_per_minute now reach their consumers via
 * `agent_config.resolve_agent_config` (see backend/app/api/v1/chat/
 * agent_runtime_config.py's module docstring for the wiring map) — none are
 * stored-only any more, so the "applies within about a minute" messaging
 * below is accurate for every field.
 *
 * Mirrors IonTierLimitsPanel.jsx's per-field "override vs default" badge +
 * inline save/reset pattern, but flat (no tier selector) and adds a model
 * `<select>` instead of a numeric input for `model_default`.
 */
import { useEffect, useState } from 'preact/hooks';
import { getAgentConfig, setAgentConfig } from '../../services/adminService.js';
import { adminEnvironment } from '../../state/userState.js';

const STAGING_VALUES = ['staging', 'stag'];

function environmentLabel() {
  const env = (adminEnvironment.value.environment || '').toLowerCase();
  if (STAGING_VALUES.includes(env)) return 'Staging';
  if (env) return env.charAt(0).toUpperCase() + env.slice(1);
  return 'Unknown';
}

// flag_key strings the backend's `overridden` map is keyed by (see
// backend/app/api/v1/chat/agent_runtime_config.py FIELD_TO_FLAG_KEY).
const FLAG_KEYS = {
  temperature_default: 'agent.temperature_default',
  model_default: 'agent.model_default',
  max_output_tokens: 'agent.max_output_tokens',
  max_iterations: 'agent.max_iterations',
  cache_ttl_default: 'cache.ttl_default',
  cache_ttl_admin: 'cache.ttl_admin',
  websearch_max_per_turn: 'websearch.max_per_turn',
  websearch_max_per_conversation: 'websearch.max_per_conversation',
  rate_limit_per_minute: 'chat.rate_limit_per_minute',
};

const NUMERIC_FIELDS = [
  {
    key: 'temperature_default',
    label: 'Temperature',
    help: 'Bedrock sampling temperature for every Ion request in this environment. Leave unset to use the provider default.',
    kind: 'float',
    min: 0,
    max: 1,
    step: 0.05,
    allowEmpty: true,
  },
  {
    key: 'max_output_tokens',
    label: 'Max output tokens',
    help: 'Hard cap on tokens the model may generate per turn.',
    kind: 'int',
    min: 1,
    max: 20000,
    step: 1,
  },
  {
    key: 'max_iterations',
    label: 'Agent max iterations',
    help: 'Cap on tool-calling round-trips per user turn — bounds a runaway self-loop.',
    kind: 'int',
    min: 1,
    max: 50,
    step: 1,
  },
  {
    key: 'websearch_max_per_turn',
    label: 'Web-search calls per turn',
    help: 'Cap on web_search tool invocations within a single turn.',
    kind: 'int',
    min: 0,
    max: 50,
    step: 1,
  },
  {
    key: 'websearch_max_per_conversation',
    label: 'Web-search calls per conversation',
    help: 'Cap on web_search tool invocations across a whole conversation.',
    kind: 'int',
    min: 0,
    max: 200,
    step: 1,
  },
  {
    key: 'rate_limit_per_minute',
    label: 'Rate limit (requests/minute)',
    help: '/chat/stream requests-per-minute cap, per user.',
    kind: 'int',
    min: 1,
    max: 1000,
    step: 1,
  },
];

const CACHE_TTL_FIELDS = [
  {
    key: 'cache_ttl_default',
    label: 'Cache TTL — non-admin',
    help: 'Anthropic prompt-cache TTL for regular (non-admin) sessions.',
  },
  {
    key: 'cache_ttl_admin',
    label: 'Cache TTL — admin',
    help: 'Anthropic prompt-cache TTL for admin sessions (long-form testing benefits from 1h).',
  },
];

export function AgentConfigPanel() {
  const [config, setConfig] = useState(null); // full GET payload
  const [drafts, setDrafts] = useState({}); // in-progress edits (string values)
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyConfig(data) {
    setConfig(data);
    const eff = data?.effective || {};
    const seeded = {};
    for (const f of NUMERIC_FIELDS) {
      seeded[f.key] = eff[f.key] != null ? String(eff[f.key]) : '';
    }
    for (const f of CACHE_TTL_FIELDS) {
      seeded[f.key] = eff[f.key] ?? '5m';
    }
    seeded.model_default = eff.model_default ?? '';
    setDrafts(seeded);
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      applyConfig(await getAgentConfig());
    } catch (err) {
      console.error('[AgentConfig] Load error:', err);
      setError(err?.message || 'Failed to load Ion agent config');
    } finally {
      setLoading(false);
    }
  }

  function flashSuccess(msg) {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  }

  function parseValue(field, raw) {
    const trimmed = String(raw ?? '').trim();
    if (trimmed === '') {
      if (field.allowEmpty) return { value: null };
      return { error: 'Enter a value.' };
    }
    const value = field.kind === 'int' ? parseInt(trimmed, 10) : parseFloat(trimmed);
    if (!Number.isFinite(value) || value < field.min || value > field.max) {
      return {
        error:
          field.kind === 'int'
            ? `Enter a whole number between ${field.min} and ${field.max}.`
            : `Enter a number between ${field.min} and ${field.max}.`,
      };
    }
    return { value };
  }

  async function saveNumeric(field) {
    const parsed = parseValue(field, drafts[field.key]);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError('');
    setSavingField(field.key);
    try {
      const next = await setAgentConfig({ [field.key]: parsed.value });
      applyConfig(next);
      flashSuccess('Saved. Applies to new requests within about a minute.');
    } catch (err) {
      console.error('[AgentConfig] Save error:', err);
      setError(err?.message || 'Failed to save');
    } finally {
      setSavingField(null);
    }
  }

  async function resetField(key) {
    setError('');
    setSavingField(key);
    try {
      const next = await setAgentConfig({ [key]: null });
      applyConfig(next);
      flashSuccess('Reverted to default. Applies within about a minute.');
    } catch (err) {
      console.error('[AgentConfig] Reset error:', err);
      setError(err?.message || 'Failed to reset');
    } finally {
      setSavingField(null);
    }
  }

  async function saveSelectField(key, value) {
    setError('');
    setSavingField(key);
    try {
      const next = await setAgentConfig({ [key]: value });
      applyConfig(next);
      flashSuccess('Saved. Applies to new requests within about a minute.');
    } catch (err) {
      console.error('[AgentConfig] Save error:', err);
      setError(err?.message || 'Failed to save');
    } finally {
      setSavingField(null);
    }
  }

  const overridden = config?.overridden || {};
  const effective = config?.effective || {};
  const allowedModels = config?.allowed_models || [];

  return (
    <div className="admin-ion-limits admin-aim-agent-config">
      <div className="admin-section-header">
        <h2>Agent Config</h2>
        <button className="admin-btn admin-btn--secondary" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      <p className="admin-demo-hint">
        Editing the <strong>{environmentLabel()}</strong> environment's Ion agent defaults —
        env-wide only, applies to every user. No deploy needed; changes apply to new requests
        within about a minute. A field showing{' '}
        <span className="admin-demo-badge admin-demo-badge--override">overridden</span>{' '}
        uses this value; <span className="admin-demo-badge">default</span> falls back to the
        environment default.
      </p>

      {success && <div className="admin-alert admin-alert--success">{success}</div>}
      {error && <div className="admin-alert admin-alert--error">{error}</div>}

      {loading ? (
        <div className="admin-loading">
          <div className="admin-spinner"></div>
          <p>Loading Ion agent config…</p>
        </div>
      ) : config ? (
        <div className="admin-demo-config-grid">
          {/* Default model — IAM-bounded <select>, never free-text. */}
          <div className="admin-demo-config-row" key="model_default">
            <div className="admin-demo-config-labels">
              <label className="admin-form-label admin-demo-config-label" htmlFor="agent-cfg-model">
                Default model
                <span
                  className={`admin-demo-badge${
                    overridden[FLAG_KEYS.model_default] ? ' admin-demo-badge--override' : ''
                  }`}
                >
                  {overridden[FLAG_KEYS.model_default] ? 'overridden' : 'default'}
                </span>
              </label>
              <p className="admin-demo-config-help">
                Default Bedrock inference profile — bounded by the backend's IAM policy (the
                only models this environment's AWS credentials can invoke).
              </p>
            </div>
            <div className="admin-demo-config-controls">
              <select
                id="agent-cfg-model"
                className="admin-demo-input admin-demo-input--model"
                value={drafts.model_default ?? ''}
                disabled={savingField === 'model_default'}
                onChange={(e) => saveSelectField('model_default', e.target.value)}
              >
                {allowedModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <button
                className="admin-btn admin-btn--secondary"
                onClick={() => resetField('model_default')}
                disabled={savingField === 'model_default' || !overridden[FLAG_KEYS.model_default]}
                title={
                  overridden[FLAG_KEYS.model_default]
                    ? 'Clear the override and revert to the environment default'
                    : 'Already using the environment default'
                }
              >
                Reset to default
              </button>
            </div>
          </div>

          {NUMERIC_FIELDS.map((field) => {
            const flagKey = FLAG_KEYS[field.key];
            const isOverride = overridden[flagKey] === true;
            const busy = savingField === field.key;
            const draftVal = drafts[field.key] ?? '';
            const currentVal = effective[field.key];
            const dirty = String(draftVal) !== String(currentVal ?? '');
            return (
              <div className="admin-demo-config-row" key={field.key}>
                <div className="admin-demo-config-labels">
                  <label
                    className="admin-form-label admin-demo-config-label"
                    htmlFor={`agent-cfg-${field.key}`}
                  >
                    {field.label}
                    <span
                      className={`admin-demo-badge${isOverride ? ' admin-demo-badge--override' : ''}`}
                    >
                      {isOverride ? 'overridden' : 'default'}
                    </span>
                  </label>
                  <p className="admin-demo-config-help">{field.help}</p>
                </div>
                <div className="admin-demo-config-controls">
                  <input
                    id={`agent-cfg-${field.key}`}
                    className="admin-demo-input"
                    type="number"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    placeholder={field.allowEmpty ? 'unset (provider default)' : undefined}
                    value={draftVal}
                    disabled={busy}
                    onInput={(e) => setDrafts({ ...drafts, [field.key]: e.target.value })}
                  />
                  <button
                    className="admin-btn admin-btn--primary"
                    onClick={() => saveNumeric(field)}
                    disabled={busy || !dirty}
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="admin-btn admin-btn--secondary"
                    onClick={() => resetField(field.key)}
                    disabled={busy || !isOverride}
                    title={
                      isOverride
                        ? 'Clear the override and revert to the environment default'
                        : 'Already using the environment default'
                    }
                  >
                    Reset to default
                  </button>
                </div>
              </div>
            );
          })}

          {CACHE_TTL_FIELDS.map((field) => {
            const flagKey = FLAG_KEYS[field.key];
            const isOverride = overridden[flagKey] === true;
            const busy = savingField === field.key;
            return (
              <div className="admin-demo-config-row" key={field.key}>
                <div className="admin-demo-config-labels">
                  <label
                    className="admin-form-label admin-demo-config-label"
                    htmlFor={`agent-cfg-${field.key}`}
                  >
                    {field.label}
                    <span
                      className={`admin-demo-badge${isOverride ? ' admin-demo-badge--override' : ''}`}
                    >
                      {isOverride ? 'overridden' : 'default'}
                    </span>
                  </label>
                  <p className="admin-demo-config-help">{field.help}</p>
                </div>
                <div className="admin-demo-config-controls">
                  <select
                    id={`agent-cfg-${field.key}`}
                    className="admin-demo-input"
                    value={drafts[field.key] ?? '5m'}
                    disabled={busy}
                    onChange={(e) => saveSelectField(field.key, e.target.value)}
                  >
                    <option value="5m">5m</option>
                    <option value="1h">1h</option>
                  </select>
                  <button
                    className="admin-btn admin-btn--secondary"
                    onClick={() => resetField(field.key)}
                    disabled={busy || !isOverride}
                    title={
                      isOverride
                        ? 'Clear the override and revert to the environment default'
                        : 'Already using the environment default'
                    }
                  >
                    Reset to default
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
